import { useMemo } from "react";

import { Spinner } from "@components/v2";

import { BenchmarkCard } from "../../benchmark-targets/benchmark-card";
import { KpiCard } from "../../benchmark-targets/kpi-card";
import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";

import { BenchmarkDetails } from "./benchmark-details";
import { BenchmarkRevChart } from "./benchmark-rev-chart";
import { ConversionLagReport } from "./conversion-lag-report";
import { FunnelReport } from "./funnel-report";

export const AdoptBenchmark = () => {
  const {
    adoptableBenchmarksMetrics,
    adoptableKpiMetrics,
    adoptedMetrics,
    adoptedBenchmarksData,
    adoptedKpiData,
    daysSelection,
    benchmarkAttributionModel,
    benchmarkAttributionWindow,
    benchmarkAccountingMode,
    setAdoptedMetrics,
    setBenchmarkAttributionModel,
    setBenchmarkAttributionWindow,
    setBenchmarkAccountingMode,
  } = useUniversalBenchmarksConfiguratorContext();

  const adoptedMetric = adoptedMetrics[0];
  const benchmarkData = adoptedBenchmarksData.find(
    (e) => e.metricId === adoptedMetric.id,
  );
  const kpiData = adoptedKpiData.find((e) => e.metricId === adoptedMetric.id);

  const adoptedSalesMetric = useMemo(() => {
    return adoptableBenchmarksMetrics.metrics.find(
      (e) => e.id === adoptedMetric.metricId,
    );
  }, [adoptedMetric, adoptableBenchmarksMetrics]);

  const isBenchmarkLoading =
    !benchmarkData || benchmarkData.status === "loading";

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex gap-6 items-center">
        <div className="min-w-[200px] max-w-[240px]">
          <BenchmarkCard
            adoptedMetrics={adoptedMetrics}
            adoptedMetric={adoptedMetric}
            adoptedMetricsData={benchmarkData}
            adoptableBenchmarksMetrics={adoptableBenchmarksMetrics.metrics}
            daysSelection={daysSelection}
            benchmarkAttributionModel={benchmarkAttributionModel}
            benchmarkAttributionWindow={benchmarkAttributionWindow}
            benchmarkAccountingMode={benchmarkAccountingMode}
            onUpdate={(value) => {
              setAdoptedMetrics(
                adoptedMetrics.map((e) =>
                  e.id === adoptedMetric.id ? value : e,
                ),
              );
            }}
            onDelete={() => {
              setAdoptedMetrics(
                adoptedMetrics.filter((e) => e.id !== adoptedMetric.id),
              );
            }}
            onBenchmarkAttributionModelChange={setBenchmarkAttributionModel}
            onBenchmarkAttributionWindowChange={setBenchmarkAttributionWindow}
            onBenchmarkAccountingModeChange={setBenchmarkAccountingMode}
          />
        </div>
        <div className="w-full">
          {isBenchmarkLoading || !adoptedSalesMetric || !benchmarkData.data ? (
            <div className="flex items-center justify-center h-full">
              <Spinner className="text-v2-cosmic/400" />
            </div>
          ) : (
            <BenchmarkDetails
              data={benchmarkData.data}
              metric={adoptedSalesMetric}
            />
          )}
        </div>
      </div>
      <div className="rounded-lg h-[320px] border border-v2-tertiary py-3 pr-2">
        {isBenchmarkLoading || !adoptedSalesMetric || !benchmarkData.data ? (
          <div className="flex items-center justify-center h-full">
            <Spinner className="text-v2-cosmic/400" />
          </div>
        ) : (
          <BenchmarkRevChart
            data={benchmarkData.data}
            metric={adoptedSalesMetric}
          />
        )}
      </div>
      <div className="flex flex-col">
        <h3 className="text-base font-bold">Benchmark Insights</h3>
        <p className="text-sm text-v2-primary">
          Your unique benchmark is built on a holistic analysis of your proven
          marketing strategies. Once adopted, benchmarks dynamically adjust to
          match the model, attribution window, accounting mode, and breakdowns
          you’re using in Northbeam.
        </p>
        <ul className="list-disc pl-6 text-sm text-v2-primary mb-0 space-y-1">
          <li>
            <strong>Boost ROI:</strong> Top-of-funnel campaigns profitable at
            lower benchmarks pave the way for high-ROI bottom-of-funnel efforts
            into higher benchmarks.
          </li>
          <li>
            <strong>Drive Profit:</strong> Tailored benchmarks for first-time,
            returning, and blended revenue drivers ensure you maximize
            profitability across all customer types.
          </li>
          <li>
            <strong>
              Make Daily Decisions That Ladder Up To Long Term Success:
            </strong>{" "}
            Act on profitable 1-day benchmarks that grow into higher,
            sustainable monthly and quarterly benchmarks, reflecting the full
            impact of your media spend.
          </li>
        </ul>
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <KpiCard
            adoptedMetric={adoptedMetric}
            adoptedMetricsData={kpiData}
            adoptableKpiMetrics={adoptableKpiMetrics}
            daysSelection={daysSelection}
          />
        </div>
        <div className="flex-1 space-y-4">
          <ConversionLagReport />
          <FunnelReport
            funnel="top"
            title="Top of Funnel"
            subtitle="Revenue by first-touch platform"
          />
          <FunnelReport
            funnel="bottom"
            title="Bottom of Funnel"
            subtitle="Revenue by last-touch platform"
          />
        </div>
      </div>
    </div>
  );
};
